import React, { useState, useEffect, useRef } from "react";
import io from 'socket.io-client';
import { API_BASE_URL } from '../../../config/api.config';

// Helper function to get PDF view URL
const getPdfViewUrl = (pdfId) => {
  if (!pdfId) return null;
  return `${API_BASE_URL}/pdfs/${pdfId}/view`;
};

const ContentForm = ({ initialData, subject, mockTests = [], onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    contentType: '',
    title: '',
    description: '',
    fileUrl: '',
    file: null,
    order: 0,
    subjectId: subject?._id || '',
    isFree: false,
    isMaterial: false,
    mockTest: '',
    totalMarks: '',
    timeLimitMin: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    stage: '',
    progress: 0,
    message: '',
    isUploading: false,
    uploadId: null
  });
  
  const socketRef = useRef(null);
  // Only consider it editing if initialData exists AND has an _id
  const isEdit = !!(initialData && initialData._id);

  useEffect(() => {
    if (initialData) {
      const pdfData = {
        contentType: initialData.contentType || '',
        title: initialData.title || '',
        description: initialData.description || '',
        fileUrl: initialData.fileUrl || '',
        fileName: initialData.fileName || null,
        fileSize: initialData.fileSize || null,
        file: null,
        order: initialData.order || 0,
        subjectId: initialData.subjectId || subject?._id || '',
        isFree: initialData.isFree || false,
        isMaterial: initialData.isMaterial || false,
        _id: initialData._id || null,
        mockTest: initialData.mockTest?._id || initialData.mockTest || '',
        totalMarks: initialData.totalMarks ?? '',
        timeLimitMin: initialData.timeLimitMin ?? ''
      };
      
      // Log PDF data for debugging
      if (pdfData.contentType === 'material') {
        console.log('PDF initialData:', {
          _id: pdfData._id,
          fileUrl: pdfData.fileUrl,
          fileName: pdfData.fileName,
          fileSize: pdfData.fileSize,
          fullInitialData: initialData
        });
      }
      
      setFormData(pdfData);
    } else {
      setFormData(prev => ({
        ...prev,
        subjectId: subject?._id || '',
        isFree: false,
        _id: null,
        mockTest: '',
        totalMarks: '',
        timeLimitMin: ''
      }));
    }
  }, [initialData, subject]);

  // Socket connection for real-time progress
  useEffect(() => {
    // Connect to socket when component mounts
    socketRef.current = io(API_BASE_URL || 'http://localhost:5001');
    
    socketRef.current.on('uploadProgress', (data) => {
      setUploadProgress({
        stage: data.stage,
        progress: data.progress,
        message: data.message,
        isUploading: data.stage !== 'complete' && data.stage !== 'error',
        uploadId: data.uploadId
      });
      
      // Auto-hide progress after completion
      if (data.stage === 'complete' || data.stage === 'error') {
        setTimeout(() => {
          setUploadProgress(prev => ({ ...prev, isUploading: false }));
        }, 3000);
      }
    });

    // Cleanup on component unmount
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 0 : 
              type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    if (!e || !e.target || !e.target.files) {
      console.error('Invalid file input event:', e);
      return;
    }
    const file = e.target.files[0];
    if (file) {
      // Validate file type for video
      if (formData.contentType === 'video' && !file.type.startsWith('video/')) {
        alert('Please select a valid video file (MP4, etc.)');
        return;
      }
      
      // Validate file type for Material
      if (formData.contentType === 'material') {
        if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
          alert('Please select a PDF file');
          return;
        }
      }
      
      // Validate file type for test
      if (formData.contentType === 'test') {
        const validTestTypes = [
          'application/pdf',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'application/vnd.ms-powerpoint',
          'application/vnd.openxmlformats-officedocument.presentationml.presentation',
          'text/plain'
        ];
        
        const validExtensions = ['.pdf', '.doc', '.docx', '.ppt', '.pptx', '.txt'];
        const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
        
        if (!validTestTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
          alert('Please select a valid test file (PDF, Word, PowerPoint, or Text file)');
          return;
        }
      }
      
      // When a new file is selected, keep the original fileUrl for reference
      // Backend will use the new file if provided, otherwise keep the existing fileUrl
      setFormData(prev => ({
        ...prev,
        file: file
        // Keep fileUrl - backend will handle: if file exists, upload it; otherwise keep fileUrl
      }));
    }
  };

  const handleRemoveFile = () => {
    // Reset to original fileUrl if editing, or clear if new
    setFormData(prev => ({
      ...prev,
      file: null,
      fileUrl: isEdit && initialData?.fileUrl ? initialData.fileUrl : ''
    }));
    // Reset file input based on content type
    let fileInputId = 'videoFile';
    if (formData.contentType === 'material') {
      fileInputId = 'pdfFile';
    } else if (formData.contentType === 'test') {
      fileInputId = 'testFile';
    }
    const fileInput = document.getElementById(fileInputId);
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Safety check
    if (!formData) {
      console.error('formData is undefined in handleSubmit');
      alert('Form data is missing. Please refresh the page and try again.');
      return;
    }
    
    // Validation
    if (!formData.contentType) {
      alert('Please select a content type');
      return;
    }

    if (formData.contentType === 'video' && !formData.file && !formData.fileUrl) {
      alert('Please upload a video file');
      return;
    }

    if (formData.contentType === 'material' && !formData.file && !formData.fileUrl && !isEdit) {
      alert('Please upload a PDF file');
      return;
    }

    if (formData.contentType === 'test' && !isEdit && !formData.file && !formData.fileUrl) {
      alert('Please upload a test file');
      return;
    }

    if (!formData.subjectId) {
      alert('Subject ID is missing');
      return;
    }

    setSubmitting(true);

    // Only show upload progress if a new file is being uploaded
    const isUploadingNewFile = !!formData.file;
    
    if (isUploadingNewFile) {
      // Reset upload progress
      setUploadProgress({
        stage: 'starting',
        progress: 0,
        message: 'Preparing upload...',
        isUploading: true,
        uploadId: null
      });
    } else {
      // No new file, just updating metadata
      setUploadProgress({
        stage: '',
        progress: 0,
        message: '',
        isUploading: false,
        uploadId: null
      });
    }

    try {
      // Set isMaterial to true when contentType is 'material'
      const isMaterial = formData.contentType === 'material';
      
      // Ensure _id is included in formData when editing
      const submitData = {
        ...formData,
        isMaterial: isMaterial || formData.isMaterial || false,
        _id: formData._id || (initialData && initialData._id) || null
      };
      
      console.log('Submitting form:', { isEdit, submitData, initialData });
      
      const result = await onSubmit(submitData, isEdit);
      if (result?.success) {
        // Don't close immediately for video uploads to show completion
        if (formData.contentType === 'video' && isUploadingNewFile) {
          setTimeout(() => {
            onClose();
          }, 2000); // Wait 2 seconds to show completion
        } else {
          onClose();
        }
      }
    } catch (error) {
      setUploadProgress({
        stage: 'error',
        progress: 0,
        message: error.message || 'Upload failed',
        isUploading: false,
        uploadId: null
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getModalTitle = () => {
    const action = isEdit ? 'Edit' : 'Add New';
    if (formData.contentType) {
      const type = formData.contentType.charAt(0).toUpperCase() + formData.contentType.slice(1);
      return `${action} ${type}`;
    }
    return `${action} Content`;
  };

  const getButtonColor = () => {
    switch (formData.contentType) {
      case 'video': return 'bg-green';
      case 'material': return 'bg-orange';
      case 'test': return 'bg-red';
      default: return 'bg-blue';
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content-form bg-white rounded-16 shadow-5">
        <div className="modal-header">
          <h4 className="modal-title">{getModalTitle()}</h4>
          <button onClick={onClose} className="modal-close-btn">
            <i className="icon-close"></i>
          </button>
        </div>
        
        <div className="modal-body">
          <form onSubmit={handleSubmit} className="content-form">
            {/* Subject Information - Read Only */}
            <div className="form-group">
              <label className="form-label">Subject</label>
              <div style={{
                padding: '10px 12px',
                backgroundColor: '#f8f9fa',
                border: '1px solid #e9ecef',
                borderRadius: '4px',
                color: '#495057',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                {subject?.icon && (
                  <i 
                    className={subject.icon} 
                    style={{ 
                      color: subject.color || '#007bff',
                      fontSize: '16px'
                    }}
                  ></i>
                )}
                <span style={{ fontWeight: '500' }}>
                  {subject?.title || 'Unknown Subject'}
                </span>
                {subject?.description && (
                  <span style={{ color: '#6c757d', fontSize: '14px' }}>
                    - {subject.description}
                  </span>
                )}
              </div>
              <small className="form-help-text">Content will be added to this subject</small>
            </div>

            {/* Content Type Selection */}
            <div className="form-group">
              <label className="form-label">Content Type *</label>
              <div style={{ display: 'flex', gap: '20px', marginTop: '8px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  padding: '10px 15px',
                  border: `2px solid ${formData.contentType === 'video' ? '#007bff' : '#e9ecef'}`,
                  borderRadius: '8px',
                  backgroundColor: formData.contentType === 'video' ? '#e3f2fd' : 'transparent',
                  transition: 'all 0.3s ease'
                }}>
                  <input
                    type="radio"
                    name="contentType"
                    value="video"
                    checked={formData.contentType === 'video'}
                    onChange={handleInputChange}
                    style={{ marginRight: '8px' }}
                  />
                   <span style={{ 
                     display: 'flex', 
                     alignItems: 'center', 
                     margin: 0,
                     color: formData.contentType === 'video' ? '#007bff' : '#333',
                     fontWeight: formData.contentType === 'video' ? '500' : 'normal'
                   }}>
                     <i className="icon-video-file" style={{ marginRight: '8px' }}></i>
                     Video
                   </span>
                </label>
                
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  padding: '10px 15px',
                  border: `2px solid ${formData.contentType === 'material' ? '#007bff' : '#e9ecef'}`,
                  borderRadius: '8px',
                  backgroundColor: formData.contentType === 'material' ? '#e3f2fd' : 'transparent',
                  transition: 'all 0.3s ease'
                }}>
                  <input
                    type="radio"
                    name="contentType"
                    value="material"
                    checked={formData.contentType === 'material'}
                    onChange={handleInputChange}
                    style={{ marginRight: '8px' }}
                  />
                   <span style={{ 
                     display: 'flex', 
                     alignItems: 'center', 
                     margin: 0,
                     color: formData.contentType === 'material' ? '#007bff' : '#333',
                     fontWeight: formData.contentType === 'material' ? '500' : 'normal'
                   }}>
                     <i className="icon-document" style={{ marginRight: '8px' }}></i>
                     Material
                   </span>
                </label>
                
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  padding: '10px 15px',
                  border: `2px solid ${formData.contentType === 'test' ? '#007bff' : '#e9ecef'}`,
                  borderRadius: '8px',
                  backgroundColor: formData.contentType === 'test' ? '#e3f2fd' : 'transparent',
                  transition: 'all 0.3s ease'
                }}>
                  <input
                    type="radio"
                    name="contentType"
                    value="test"
                    checked={formData.contentType === 'test'}
                    onChange={handleInputChange}
                    style={{ marginRight: '8px' }}
                  />
                   <span style={{ 
                     display: 'flex', 
                     alignItems: 'center', 
                     margin: 0,
                     color: formData.contentType === 'test' ? '#007bff' : '#333',
                     fontWeight: formData.contentType === 'test' ? '500' : 'normal'
                   }}>
                     <i className="icon-check" style={{ marginRight: '8px' }}></i>
                     Test
                   </span>
                </label>
              </div>
            </div>

            {/* Free Content Toggle - Show only when content type is selected */}
            {formData.contentType && (
              <div className="form-group">
                <div style={{ 
                  padding: '15px', 
                  backgroundColor: '#f8f9fa', 
                  border: '1px solid #e9ecef', 
                  borderRadius: '8px',
                  marginTop: '20px'
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    margin: 0,
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    <input
                      type="checkbox"
                      name="isFree"
                      checked={formData.isFree}
                      onChange={handleInputChange}
                      style={{ 
                        marginRight: '10px',
                        width: '18px',
                        height: '18px',
                        accentColor: '#007bff'
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ color: formData.isFree ? '#007bff' : '#495057' }}>
                          🆓 Make this content FREE for all users
                        </span>
                        {formData.isFree && (
                          <span style={{
                            fontSize: '12px',
                            backgroundColor: '#e3f2fd',
                            color: '#1976d2',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontWeight: '500'
                          }}>
                            FREE
                          </span>
                        )}
                      </div>
                      <div style={{ 
                        fontSize: '12px', 
                        color: '#6c757d', 
                        marginTop: '4px',
                        lineHeight: '1.4'
                      }}>
                        {formData.isFree 
                          ? '✅ Users can access this content without purchasing the exam'
                          : '🔒 Users need to purchase the exam to access this content'
                        }
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {/* Order Field - Show only when content type is selected */}
            {formData.contentType && (
              <div className="form-group">
                <label className="form-label">Display Order</label>
                <input 
                  type="number" 
                  name="order" 
                  value={formData.order}
                  onChange={handleInputChange}
                  placeholder="0"
                  min="0"
                  className="form-input"
                  style={{ width: '120px' }}
                />
                <small className="form-help-text">
                  Content with lower numbers will appear first. Use 0 for default ordering.
                </small>
              </div>
            )}

            {/* Video Content Fields */}
            {formData.contentType === 'video' && (
              <>
                <div className="form-group">
                  <label className="form-label">Video Title *</label>
                  <input 
                    type="text" 
                    name="title" 
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Enter video title"
                    className="form-input"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Video Description *</label>
                  <textarea 
                    name="description" 
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Brief description of the video content"
                    className="form-textarea"
                    rows="3"
                    required
                  ></textarea>
                </div>
                
                {/* Show existing video when editing */}
                {isEdit && formData.fileUrl && !formData.file && (
                  <div className="form-group">
                    <label className="form-label">Current Video</label>
                    <div style={{
                      marginTop: '8px',
                      padding: '15px',
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #e9ecef',
                      borderRadius: '8px'
                    }}>
                      <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <i className="icon-video-file" style={{ color: '#28a745', fontSize: '20px' }}></i>
                        <span style={{ fontWeight: '500', color: '#495057' }}>Current Video</span>
                      </div>
                      <video 
                        controls 
                        style={{
                          width: '100%',
                          maxHeight: '300px',
                          borderRadius: '4px',
                          backgroundColor: '#000'
                        }}
                        src={formData.fileUrl}
                        onError={(e) => {
                          console.error('Error loading video:', e);
                          e.target.style.display = 'none';
                          const errorDiv = e.target.nextSibling;
                          if (errorDiv) {
                            errorDiv.style.display = 'block';
                          }
                        }}
                      >
                        Your browser does not support the video tag.
                      </video>
                      <div style={{ 
                        display: 'none',
                        padding: '10px',
                        backgroundColor: '#fff3cd',
                        border: '1px solid #ffc107',
                        borderRadius: '4px',
                        marginTop: '10px',
                        color: '#856404'
                      }}>
                        <small>Unable to load video preview. The video will be retained if no new file is uploaded.</small>
                      </div>
                      <small style={{ 
                        display: 'block', 
                        marginTop: '8px', 
                        color: '#6c757d',
                        fontSize: '12px'
                      }}>
                        This video will be retained unless you upload a new one below.
                      </small>
                    </div>
                  </div>
                )}
                
                <div className="form-group">
                  <label className="form-label">
                    {isEdit ? 'Upload New Video File (MP4) - Optional' : 'Upload Video File (MP4) *'}
                  </label>
                  <div style={{ marginTop: '8px' }}>
                    <input 
                      type="file" 
                      id="videoFile"
                      accept="video/*"
                      onChange={handleFileChange}
                      className="file-input"
                      style={{ display: 'none' }}
                      required={!isEdit}
                    />
                    <label 
                      htmlFor="videoFile" 
                      className="file-upload-btn"
                      style={{
                        display: 'inline-block',
                        padding: '10px 20px',
                        backgroundColor: '#f8f9fa',
                        border: '2px dashed #ddd',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        textAlign: 'center',
                        transition: 'all 0.3s ease',
                        color: '#495057',
                        fontSize: '14px'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.backgroundColor = '#e9ecef';
                        e.target.style.borderColor = '#adb5bd';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.backgroundColor = '#f8f9fa';
                        e.target.style.borderColor = '#ddd';
                      }}
                    >
                      <i className="icon-video-file" style={{ marginRight: '8px' }}></i>
                      {formData.file ? formData.file.name : (isEdit ? 'Click to upload new video file' : 'Click to upload video file')}
                    </label>
                    {formData.file && (
                      <div style={{ 
                        marginTop: '10px', 
                        padding: '8px', 
                        backgroundColor: '#f8f9fa', 
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <small style={{ color: '#28a745' }}>
                          <i className="icon-check" style={{ marginRight: '5px' }}></i>
                          Selected: {formData.file.name} ({(formData.file.size / (1024 * 1024)).toFixed(2)} MB)
                        </small>
                        {isEdit && (
                          <button
                            type="button"
                            onClick={handleRemoveFile}
                            style={{
                              padding: '4px 12px',
                              backgroundColor: '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              marginLeft: '10px'
                            }}
                            onMouseOver={(e) => {
                              e.target.style.backgroundColor = '#c82333';
                            }}
                            onMouseOut={(e) => {
                              e.target.style.backgroundColor = '#dc3545';
                            }}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    )}
                    {isEdit && (
                      <small className="form-help-text" style={{ display: 'block', marginTop: '8px' }}>
                        Leave empty to keep the current video, or upload a new file to replace it.
                      </small>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Material Content Fields */}
            {(formData.contentType === 'pdf' || formData.contentType === 'material') && (
              <>
                <div className="form-group">
                  <label className="form-label">Material Title *</label>
                  <input 
                    type="text" 
                    name="title" 
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Enter PDF title"
                    className="form-input"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea 
                    name="description" 
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Brief description of the PDF"
                    className="form-textarea"
                    rows="3"
                  ></textarea>
                </div>
                
                {/* Show existing PDF when editing */}
                {isEdit && (formData.fileUrl || initialData?.fileUrl) && !formData.file && (
                  <div className="form-group">
                    <label className="form-label">Current PDF</label>
                    <div style={{
                      marginTop: '8px',
                      padding: '15px',
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #e9ecef',
                      borderRadius: '8px'
                    }}>
                      <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <i className="icon-document" style={{ color: '#ff9800', fontSize: '20px' }}></i>
                        <span style={{ fontWeight: '500', color: '#495057' }}>Current PDF</span>
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px',
                        backgroundColor: 'white',
                        borderRadius: '4px',
                        border: '1px solid #dee2e6'
                      }}>
                        <i className="icon-document" style={{ color: '#dc3545', fontSize: '24px' }}></i>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '500', color: '#495057', marginBottom: '4px' }}>
                            {formData.fileName || initialData?.fileName || formData.title || 'PDF Document'}
                          </div>
                          {(() => {
                            const fileSize = formData.fileSize || initialData?.fileSize;
                            if (fileSize && fileSize > 0) {
                              const sizeInMB = fileSize / (1024 * 1024);
                              const sizeInKB = fileSize / 1024;
                              return (
                                <div style={{ fontSize: '12px', color: '#6c757d' }}>
                                  Size: {sizeInMB >= 1 
                                    ? `${sizeInMB.toFixed(2)} MB` 
                                    : `${sizeInKB.toFixed(2)} KB`}
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                        {(formData._id || initialData?._id) && (
                          <button
                            type="button"
                            onClick={async (e) => {
                              e.preventDefault();
                              const pdfId = formData._id || initialData?._id;
                              if (!pdfId) return;
                              
                              try {
                                // Get auth token for the request
                                const userData = localStorage.getItem('user');
                                const token = userData ? JSON.parse(userData).token : null;
                                
                                // Check if fileUrl is a direct HTTP URL (S3 or external)
                                const directUrl = formData.fileUrl || initialData?.fileUrl;
                                
                                if (directUrl && directUrl.startsWith('http')) {
                                  // For S3 URLs, we need to get a signed URL via the view endpoint
                                  const response = await fetch(`${API_BASE_URL}/api/pdfs/${pdfId}/view`, {
                                    headers: token ? {
                                      'Authorization': `Bearer ${token}`
                                    } : {}
                                  });
                                  
                                  if (response.ok) {
                                    const contentType = response.headers.get('content-type');
                                    
                                    // If it's JSON, it contains a signed URL
                                    if (contentType && contentType.includes('application/json')) {
                                      const data = await response.json();
                                      if (data.success && data.fileUrl) {
                                        // Open the signed URL in a new tab
                                        window.open(data.fileUrl, '_blank', 'noopener,noreferrer');
                                        return;
                                      }
                                    }
                                  }
                                  
                                  // Fallback: try direct URL (might work for public S3 URLs)
                                  window.open(directUrl, '_blank', 'noopener,noreferrer');
                                } else {
                                  // For local files, open the view endpoint directly
                                  // The endpoint will stream the file
                                  const viewUrl = `${API_BASE_URL}/api/pdfs/${pdfId}/view`;
                                  window.open(viewUrl, '_blank', 'noopener,noreferrer');
                                }
                              } catch (error) {
                                console.error('Error opening PDF:', error);
                                // Fallback: try direct fileUrl or view endpoint
                                const directUrl = formData.fileUrl || initialData?.fileUrl;
                                if (directUrl && directUrl.startsWith('http')) {
                                  window.open(directUrl, '_blank', 'noopener,noreferrer');
                                } else {
                                  const viewUrl = `${API_BASE_URL}/api/pdfs/${pdfId}/view`;
                                  window.open(viewUrl, '_blank', 'noopener,noreferrer');
                                }
                              }
                            }}
                            style={{
                              padding: '8px 16px',
                              backgroundColor: '#007bff',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '14px',
                              fontWeight: '500',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              transition: 'background-color 0.2s',
                              cursor: 'pointer'
                            }}
                            onMouseOver={(e) => {
                              e.target.style.backgroundColor = '#0056b3';
                            }}
                            onMouseOut={(e) => {
                              e.target.style.backgroundColor = '#007bff';
                            }}
                          >
                            <i className="icon-external-link" style={{ fontSize: '12px' }}></i>
                            View PDF
                          </button>
                        )}
                      </div>
                      {!(formData.fileUrl || initialData?.fileUrl) && (
                        <div style={{
                          padding: '10px',
                          backgroundColor: '#fff3cd',
                          border: '1px solid #ffc107',
                          borderRadius: '4px',
                          marginTop: '10px',
                          color: '#856404',
                          fontSize: '12px'
                        }}>
                          <i className="icon-alert" style={{ marginRight: '5px' }}></i>
                          PDF file URL not available. You may need to upload a new file.
                        </div>
                      )}
                      <small style={{ 
                        display: 'block', 
                        marginTop: '8px', 
                        color: '#6c757d',
                        fontSize: '12px'
                      }}>
                        This PDF will be retained unless you upload a new one below.
                      </small>
                    </div>
                  </div>
                )}
                
                <div className="form-group">
                  <label className="form-label">
                    {isEdit ? 'Upload New PDF File - Optional' : 'Upload Material File (PDF, DOC, etc.) *'}
                  </label>
                  <div style={{ marginTop: '8px' }}>
                    <input 
                      type="file" 
                      id="pdfFile"
                      accept=".pdf,.doc,.docx,.ppt,.pptx,.txt"
                      onChange={handleFileChange}
                      className="file-input"
                      style={{ display: 'none' }}
                      required={!isEdit}
                    />
                    <label 
                      htmlFor="pdfFile" 
                      className="file-upload-btn"
                      style={{
                        display: 'inline-block',
                        padding: '10px 20px',
                        backgroundColor: '#f8f9fa',
                        border: '2px dashed #ddd',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        textAlign: 'center',
                        transition: 'all 0.3s ease',
                        color: '#495057',
                        fontSize: '14px'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.backgroundColor = '#e9ecef';
                        e.target.style.borderColor = '#adb5bd';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.backgroundColor = '#f8f9fa';
                        e.target.style.borderColor = '#ddd';
                      }}
                    >
                      <i className="icon-document" style={{ marginRight: '8px' }}></i>
                      {formData.file ? formData.file.name : (isEdit ? 'Click to upload new PDF file' : 'Click to upload PDF file')}
                    </label>
                    {formData.file && (
                      <div style={{ 
                        marginTop: '10px', 
                        padding: '8px', 
                        backgroundColor: '#f8f9fa', 
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <small style={{ color: '#28a745' }}>
                          <i className="icon-check" style={{ marginRight: '5px' }}></i>
                          Selected: {formData.file.name} ({(formData.file.size / (1024 * 1024)).toFixed(2)} MB)
                        </small>
                        {isEdit && (
                          <button
                            type="button"
                            onClick={handleRemoveFile}
                            style={{
                              padding: '4px 12px',
                              backgroundColor: '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              marginLeft: '10px'
                            }}
                            onMouseOver={(e) => {
                              e.target.style.backgroundColor = '#c82333';
                            }}
                            onMouseOut={(e) => {
                              e.target.style.backgroundColor = '#dc3545';
                            }}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <small className="form-help-text">
                    {isEdit 
                      ? 'Leave empty to keep the current PDF, or upload a new file to replace it.'
                      : 'Supported formats: PDF, Word (.doc, .docx), PowerPoint (.ppt, .pptx), Text (.txt)'
                    }
                  </small>
                </div>
              </>
            )}

            {/* Test Content Fields (mock test PDF: isMockTest, mockTest package, totalMarks, timeLimitMin) */}
            {formData.contentType === 'test' && (
              <>
                <div className="form-group">
                  <label className="form-label">Mock Test Package</label>
                  <select
                    name="mockTest"
                    value={formData.mockTest || ''}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="">— Select package (optional) —</option>
                    {(mockTests || []).map((pkg) => (
                      <option key={pkg._id} value={pkg._id}>{pkg.title}</option>
                    ))}
                  </select>
                  <small className="form-help-text">Link this test to a mock test package so it appears under that package.</small>
                </div>
                <div className="form-row">
                  <div className="form-group half">
                    <label className="form-label">Total Marks</label>
                    <input type="number" name="totalMarks" value={formData.totalMarks} onChange={handleInputChange} placeholder="e.g., 100" className="form-input" min="0" />
                  </div>
                  <div className="form-group half">
                    <label className="form-label">Time Limit (minutes)</label>
                    <input type="number" name="timeLimitMin" value={formData.timeLimitMin} onChange={handleInputChange} placeholder="e.g., 180" className="form-input" min="0" />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Test Title *</label>
                  <input 
                    type="text" 
                    name="title" 
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Enter test title"
                    className="form-input"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea 
                    name="description" 
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Brief description of the test"
                    className="form-textarea"
                    rows="3"
                  ></textarea>
                </div>
                
                {/* Show existing Test PDF when editing */}
                {isEdit && (formData.fileUrl || initialData?.fileUrl) && !formData.file && (
                  <div className="form-group">
                    <label className="form-label">Current Test File</label>
                    <div style={{
                      marginTop: '8px',
                      padding: '15px',
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #e9ecef',
                      borderRadius: '8px'
                    }}>
                      <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <i className="icon-document" style={{ color: '#dc3545', fontSize: '20px' }}></i>
                        <span style={{ fontWeight: '500', color: '#495057' }}>Current Test File</span>
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px',
                        backgroundColor: 'white',
                        borderRadius: '4px',
                        border: '1px solid #dee2e6'
                      }}>
                        <i className="icon-document" style={{ color: '#dc3545', fontSize: '24px' }}></i>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '500', color: '#495057', marginBottom: '4px' }}>
                            {formData.fileName || initialData?.fileName || formData.title || 'Test File'}
                          </div>
                          {(() => {
                            const fileSize = formData.fileSize || initialData?.fileSize;
                            if (fileSize && fileSize > 0) {
                              const sizeInMB = fileSize / (1024 * 1024);
                              const sizeInKB = fileSize / 1024;
                              return (
                                <div style={{ fontSize: '12px', color: '#6c757d' }}>
                                  Size: {sizeInMB >= 1 
                                    ? `${sizeInMB.toFixed(2)} MB` 
                                    : `${sizeInKB.toFixed(2)} KB`}
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                        {(formData._id || initialData?._id) && (
                          <button
                            type="button"
                            onClick={async (e) => {
                              e.preventDefault();
                              const pdfId = formData._id || initialData?._id;
                              if (!pdfId) return;
                              
                              try {
                                // Get auth token for the request
                                const userData = localStorage.getItem('user');
                                const token = userData ? JSON.parse(userData).token : null;
                                
                                // Check if fileUrl is a direct HTTP URL (S3 or external)
                                const directUrl = formData.fileUrl || initialData?.fileUrl;
                                
                                if (directUrl && directUrl.startsWith('http')) {
                                  // For S3 URLs, we need to get a signed URL via the view endpoint
                                  const response = await fetch(`${API_BASE_URL}/api/pdfs/${pdfId}/view`, {
                                    headers: token ? {
                                      'Authorization': `Bearer ${token}`
                                    } : {}
                                  });
                                  
                                  if (response.ok) {
                                    const contentType = response.headers.get('content-type');
                                    
                                    // If it's JSON, it contains a signed URL
                                    if (contentType && contentType.includes('application/json')) {
                                      const data = await response.json();
                                      if (data.success && data.fileUrl) {
                                        // Open the signed URL in a new tab
                                        window.open(data.fileUrl, '_blank', 'noopener,noreferrer');
                                        return;
                                      }
                                    }
                                  }
                                  
                                  // Fallback: try direct URL (might work for public S3 URLs)
                                  window.open(directUrl, '_blank', 'noopener,noreferrer');
                                } else {
                                  // For local files, open the view endpoint directly
                                  // The endpoint will stream the file
                                  const viewUrl = `${API_BASE_URL}/api/pdfs/${pdfId}/view`;
                                  window.open(viewUrl, '_blank', 'noopener,noreferrer');
                                }
                              } catch (error) {
                                console.error('Error opening test file:', error);
                                // Fallback: try direct fileUrl or view endpoint
                                const directUrl = formData.fileUrl || initialData?.fileUrl;
                                if (directUrl && directUrl.startsWith('http')) {
                                  window.open(directUrl, '_blank', 'noopener,noreferrer');
                                } else {
                                  const viewUrl = `${API_BASE_URL}/api/pdfs/${pdfId}/view`;
                                  window.open(viewUrl, '_blank', 'noopener,noreferrer');
                                }
                              }
                            }}
                            style={{
                              padding: '8px 16px',
                              backgroundColor: '#007bff',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '14px',
                              fontWeight: '500',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              transition: 'background-color 0.2s',
                              cursor: 'pointer'
                            }}
                            onMouseOver={(e) => {
                              e.target.style.backgroundColor = '#0056b3';
                            }}
                            onMouseOut={(e) => {
                              e.target.style.backgroundColor = '#007bff';
                            }}
                          >
                            <i className="icon-external-link" style={{ fontSize: '12px' }}></i>
                            View File
                          </button>
                        )}
                      </div>
                      {!(formData.fileUrl || initialData?.fileUrl) && (
                        <div style={{
                          padding: '10px',
                          backgroundColor: '#fff3cd',
                          border: '1px solid #ffc107',
                          borderRadius: '4px',
                          marginTop: '10px',
                          color: '#856404',
                          fontSize: '12px'
                        }}>
                          <i className="icon-alert" style={{ marginRight: '5px' }}></i>
                          Test file URL not available. You may need to upload a new file.
                        </div>
                      )}
                      <small style={{ 
                        display: 'block', 
                        marginTop: '8px', 
                        color: '#6c757d',
                        fontSize: '12px'
                      }}>
                        This test file will be retained unless you upload a new one below.
                      </small>
                    </div>
                  </div>
                )}
                
                <div className="form-group">
                  <label className="form-label">{isEdit ? 'Upload New Test File - Optional' : 'Upload Test File (PDF, DOC, etc.) *'}</label>
                  <div style={{ marginTop: '8px' }}>
                    <input 
                      type="file" 
                      id="testFile"
                      accept=".pdf,.doc,.docx,.ppt,.pptx,.txt"
                      onChange={handleFileChange}
                      className="file-input"
                      style={{ display: 'none' }}
                      required={!isEdit}
                    />
                    <label 
                      htmlFor="testFile" 
                      className="file-upload-btn"
                      style={{
                        display: 'inline-block',
                        padding: '10px 20px',
                        backgroundColor: '#f8f9fa',
                        border: '2px dashed #ddd',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        textAlign: 'center',
                        transition: 'all 0.3s ease',
                        color: '#495057',
                        fontSize: '14px'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.backgroundColor = '#e9ecef';
                        e.target.style.borderColor = '#adb5bd';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.backgroundColor = '#f8f9fa';
                        e.target.style.borderColor = '#ddd';
                      }}
                    >
                      <i className="icon-document" style={{ marginRight: '8px' }}></i>
                      {formData.file ? formData.file.name : 'Click to upload test file'}
                    </label>
                    {formData.file && (
                      <div style={{ 
                        marginTop: '10px', 
                        padding: '8px', 
                        backgroundColor: '#f8f9fa', 
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <small style={{ color: '#28a745' }}>
                          <i className="icon-check" style={{ marginRight: '5px' }}></i>
                          Selected: {formData.file.name} ({(formData.file.size / (1024 * 1024)).toFixed(2)} MB)
                        </small>
                        {isEdit && (
                          <button
                            type="button"
                            onClick={handleRemoveFile}
                            style={{
                              padding: '4px 12px',
                              backgroundColor: '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              marginLeft: '10px'
                            }}
                            onMouseOver={(e) => {
                              e.target.style.backgroundColor = '#c82333';
                            }}
                            onMouseOut={(e) => {
                              e.target.style.backgroundColor = '#dc3545';
                            }}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <small className="form-help-text">
                    {isEdit 
                      ? 'Leave empty to keep the current test file, or upload a new file to replace it.'
                      : 'Supported formats: PDF, Word (.doc, .docx), PowerPoint (.ppt, .pptx), Text (.txt)'
                    }
                  </small>
                </div>
              </>
            )}

            {/* Upload Progress Indicator */}
            {uploadProgress.isUploading && (
              <div className="upload-progress-container" style={{
                marginTop: '20px',
                padding: '15px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #e9ecef'
              }}>
                <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                    {uploadProgress.message}
                  </span>
                  <span style={{ fontSize: '14px', color: '#6c757d' }}>
                    {uploadProgress.progress}%
                  </span>
                </div>
                
                {/* Progress Bar */}
                <div style={{
                  width: '100%',
                  height: '8px',
                  backgroundColor: '#e9ecef',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%',
                    width: `${uploadProgress.progress}%`,
                    backgroundColor: uploadProgress.stage === 'error' ? '#dc3545' : 
                                   uploadProgress.stage === 'complete' ? '#28a745' : '#007bff',
                    borderRadius: '4px',
                    transition: 'width 0.3s ease'
                  }}></div>
                </div>
                
                {/* Stage Indicator */}
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#6c757d' }}>
                  Stage: <span style={{ 
                    fontWeight: '500',
                    color: uploadProgress.stage === 'error' ? '#dc3545' : 
                           uploadProgress.stage === 'complete' ? '#28a745' : '#007bff'
                  }}>
                    {uploadProgress.stage.charAt(0).toUpperCase() + uploadProgress.stage.slice(1)}
                  </span>
                </div>
              </div>
            )}
            
            <div className="modal-footer">
              <button type="button" onClick={onClose} className="btn-cancel">
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={submitting || !formData.contentType || uploadProgress.isUploading}
                className={`btn-primary ${getButtonColor()}`}
              >
                {uploadProgress.isUploading ? 
                  `${uploadProgress.stage === 'complete' ? 'Completed!' : 'Uploading...'}` :
                  submitting ? 'Saving...' : 
                  (isEdit ? 'Update Content' : 'Add Content')
                }
              </button>
            </div>
          </form>
        </div>
      </div>

    </div>
  );
};

export default ContentForm; 